import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import {
  FiStar, FiSearch, FiCheckCircle, FiXCircle, FiMessageSquare,
  FiUser, FiEye, FiAlertCircle, FiTrendingUp
} from 'react-icons/fi';
import { toast } from 'react-toastify';

const AdminReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchReviews = useCallback(async () => {
    if (mountedRef.current) setLoading(true);
    try {
      const res = await adminAPI.getReviews();
      const data = res?.data;

      if (data && data.success) {
        if (mountedRef.current) setReviews(Array.isArray(data.data) ? data.data : []);
      } else {
        if (mountedRef.current) setReviews([]);
        toast.info('Aucune donnée d\'avis renvoyée par le serveur.');
      }
    } catch (error) {
      console.error('fetchReviews error', error);
      toast.error('Erreur lors du chargement des avis');
      if (mountedRef.current) setReviews([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleApprove = async (reviewId) => {
    try {
      await adminAPI.approveReview(reviewId);
      toast.success('Avis approuvé');
      fetchReviews();
    } catch (error) {
      console.error('handleApprove error', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleReject = async (reviewId) => {
    if (!window.confirm('Rejeter cet avis?')) return;
    try {
      await adminAPI.rejectReview(reviewId);
      toast.success('Avis rejeté');
      fetchReviews();
    } catch (error) {
      console.error('handleReject error', error);
      toast.error('Erreur lors du rejet');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Supprimer définitivement cet avis?')) return;
    try {
      await adminAPI.deleteReview(reviewId);
      toast.success('Avis supprimé');
      setReviews(prev => prev.filter(r => r._id !== reviewId));
    } catch (error) {
      console.error('handleDelete error', error);
      toast.error('Erreur lors de la suppression');
      fetchReviews();
    }
  };

  const renderStars = (rating) => {
    const r = safeNum(rating);
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            className={`${star <= r ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
            size={16}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };

    const labels = {
      approved: 'Approuvé',
      pending: 'En attente',
      rejected: 'Rejeté'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredReviews = reviews.filter(review => {
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (review.userId?.firstName || '').toLowerCase().includes(q) ||
      (review.userId?.lastName || '').toLowerCase().includes(q) ||
      (review.dealId?.title || '').toLowerCase().includes(q) ||
      (review.comment || '').toLowerCase().includes(q);

    const matchesRating = ratingFilter === 'all' || safeNum(review.rating) === parseInt(ratingFilter, 10);
    const matchesStatus = statusFilter === 'all' || (review.status || '') === statusFilter;

    return matchesSearch && matchesRating && matchesStatus;
  });

  const stats = {
    total: reviews.length,
    pending: reviews.filter(r => r.status === 'pending').length,
    approved: reviews.filter(r => r.status === 'approved').length,
    rejected: reviews.filter(r => r.status === 'rejected').length,
    averageRating: reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + safeNum(r.rating), 0) / reviews.length).toFixed(1)
      : 0,
    fiveStars: reviews.filter(r => safeNum(r.rating) === 5).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Avis</h1>
            <p className="text-gray-600 mt-1">Modérez et gérez les avis clients</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher avis, client, deal..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Toutes les notes</option>
              <option value="5">5 étoiles</option>
              <option value="4">4 étoiles</option>
              <option value="3">3 étoiles</option>
              <option value="2">2 étoiles</option>
              <option value="1">1 étoile</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvés</option>
              <option value="rejected">Rejetés</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Avis</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FiMessageSquare className="text-blue-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Note Moyenne</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.averageRating} ⭐</p>
            </div>
            <FiStar className="text-yellow-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En Attente</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <FiAlertCircle className="text-yellow-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approuvés</p>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
            </div>
            <FiCheckCircle className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejetés</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            </div>
            <FiXCircle className="text-red-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">5 Étoiles</p>
              <p className="text-2xl font-bold text-primary-600">{stats.fiveStars}</p>
            </div>
            <FiTrendingUp className="text-primary-500 text-2xl" />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {filteredReviews.length > 0 ? (
          filteredReviews.map((review) => (
            <div key={review._id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <FiUser className="text-primary-600 text-xl" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold text-gray-900">
                        {review.userId?.firstName} {review.userId?.lastName}
                      </h3>
                      {renderStars(review.rating)}
                    </div>
                    <p className="text-sm text-gray-600">
                      Deal: {review.dealId?.title || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(review.createdAt).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(review.status)}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-gray-700">{review.comment}</p>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <FiEye />
                  <span>Visible publiquement: {review.status === 'approved' ? 'Oui' : 'Non'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedReview(review);
                      setShowDetailsModal(true);
                    }}
                    className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    Voir détails
                  </button>
                  {review.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(review._id)}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                      >
                        Approuver
                      </button>
                      <button
                        onClick={() => handleReject(review._id)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Rejeter
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(review._id)}
                    className="px-3 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            <FiMessageSquare className="mx-auto text-4xl mb-2" />
            <p>Aucun avis trouvé</p>
          </div>
        )}
      </div>

      {showDetailsModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Détails de l'avis</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Informations</h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-600">Client:</span>{' '}
                      <span className="font-medium">
                        {selectedReview.userId?.firstName} {selectedReview.userId?.lastName}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-600">Email:</span> {selectedReview.userId?.email}
                    </p>
                    <p>
                      <span className="text-gray-600">Deal:</span> {selectedReview.dealId?.title}
                    </p>
                    <p>
                      <span className="text-gray-600">Note:</span>{' '}
                      {renderStars(selectedReview.rating)}
                    </p>
                    <p>
                      <span className="text-gray-600">Statut:</span>{' '}
                      {getStatusBadge(selectedReview.status)}
                    </p>
                    <p>
                      <span className="text-gray-600">Date:</span>{' '}
                      {new Date(selectedReview.createdAt).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Commentaire</h3>
                  <p className="text-sm text-gray-700">{selectedReview.comment}</p>
                </div>

                {selectedReview.status === 'pending' && (
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => {
                        handleApprove(selectedReview._id);
                        setShowDetailsModal(false);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approuver
                    </button>
                    <button
                      onClick={() => {
                        handleReject(selectedReview._id);
                        setShowDetailsModal(false);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Rejeter
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviewsManagement;